/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps }  from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StepperContext } from "@monsma/App/Widgets/CheckoutStepper/StepperContext";
import { screen } from "@monsma/XDL/Helpers";
import ShoppingCart from "@monsma/XDL/Icons/ShoppingCart";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartPage: getPageLinkByPageType(state, "CartPage"),
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutStepperStyles {
    stepperWrapperStyle: InjectableCss;
    wrapperStyle: InjectableCss;
    stepperItemWrapper: InjectableCss;
    cartIcon?: IconPresentationProps;
    currentStepText?: TypographyPresentationProps;
}

export const styles: CheckoutStepperStyles = {
    stepperWrapperStyle: {
        css: css`
            ${screen("lt", "sm")} {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
        `
    },
    wrapperStyle: {
        css: css`
            // visual style
            background: white;
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 6px;
            font-size: 13px;
            font-weight: bold;

            // visual appearance
            position: relative;
            display: flex;
            padding: 0;
            list-style: none;
            ${screen("lt", "sm")} {
                justify-content: space-between;
                background: transparent;
                border: 0;
                min-width: 113px;
                &:before {
                    content: "";
                    border-top: 1px solid ${getColor("common.ui12")};
                    width: 100%;
                    position: absolute;
                    top: 10px;
                }
            }
        `,
    },
    stepperItemWrapper: {
        css: css`
            //border: 1px solid black;
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            flex-grow: 1;
            height: 32px;
            border-right: 1px solid ${getColor("common.ui12")};
            ${screen("lt", "sm")} {
                font-size: 0;
                border-radius: 50%;
                border: 1px solid ${getColor("common.ui12")};
                flex-grow: 0;
                width: 20px;
                height: 20px;
                background: white;
            }

            &:after,
            &:before {
                content: "";
                position: absolute;
                top: 50%;
                left: calc(100% - 1px);
                transform: translateY(-50%);
                border-width: 10px 0 10px 6px;
                border-color: transparent transparent transparent white;
                border-style: solid;
                z-index: 1;
                ${screen("lt", "sm")} {
                    content: none;
                }
            }
            &:before {
                left: 100%;
                border-color: transparent transparent transparent ${getColor("common.ui12")};
            }

            /* div {
                display: block;
                ${screen("on", "md")} {
                    font-size: 0;
                    &::first-letter {
                        font-size: 13px;
                    }
                }
            } */
            &.active,
            &.completed {
                // border-color: transparent;
                background: ${getColor("primary.main")};
                color: white;
                &:before {
                    border-color: transparent transparent transparent ${getColor("common.ui12")};
                }
                &:after {
                    border-color: transparent transparent transparent ${getColor("primary.main")};
                    ${screen("lt", "sm")} {
                        border-color: ${getColor("primary.main")} transparent transparent transparent;
                    }
                }
            }
            &.completed {
                background: ${getColor("secondary.main")};
                &:after {
                    border-color: transparent transparent transparent ${getColor("secondary.main")};
                    ${screen("lt", "sm")} {
                        border-color: ${getColor("secondary.main")} transparent transparent transparent;
                    }
                }
            }
            &.clickable {
                cursor: pointer;
                &:hover {
                    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
                }
            }

            &:first-child {
                border-radius: 6px 0 0 6px;
                max-width: 150px;
                svg {
                    margin: auto;
                }
                ${screen("lt", "sm")} {
                    border-radius: 50%;
                    svg {
                        width: 10px;
                        height: 10px;
                    }
                    display: none;
                }
                ${screen("lte", "sm")} {
                    max-width: initial;
                }
            }
            &:last-child {
                border-radius: 0 6px 6px 0;
                border-right-width: 0;
                
                &:before,
                &:after {
                    border-color: transparent;
                }

                ${screen("lt", "sm")} {
                    border-radius: 50%;
                    border-right-width: 1px; 
                }
            }
        `,
    },
    cartIcon: {
        src: ShoppingCart,
        size: 20,
        color: "white",
    },
    currentStepText: {
        variant: "h4",
        css: css`
            display: none;
            margin-bottom: 0;
            ${screen("lt", "sm")} {
                display: block; 
            }
        `
    }
};

const CheckoutStepper: React.FC<Props> = ({ history, isPickUp, cartPage, checkoutShippingPage }) => {
    const { currentStep, maxStep, setCurrentStep } = useContext(StepperContext);

    const useCartIcon = true;

    // if pickup empty hide and move to the next step
    const steps: any = ["Cart", "Address", isPickUp ? "" : "Shipping", "Payment", "Review"];

    const goTo = (i: number) => {
        if (i === 0) {
            history.push(cartPage?.url as string);
        } else if (i === 1) {
            history.push(checkoutShippingPage?.url as string);
        }
        setCurrentStep(i);
    };

    const currentStepText = steps[currentStep];

    return (
        <StyledWrapper {...styles.stepperWrapperStyle}>
            <Typography {...styles.currentStepText}>
                {translate(currentStepText)}
            </Typography>
            <Typography as="ul" {...styles.wrapperStyle}>
                {steps &&
                    steps.length > 0 &&
                    steps.map((item: any, index: number) => {
                        return !item ? null : (
                            <Typography
                                as="li"
                                key={`stepper${item + index}`}
                                {...styles.stepperItemWrapper}
                                className={`${currentStep === index && "active"} ${
                                    currentStep > index && "completed"
                                } ${index <= maxStep && "clickable"}`}
                                onClick={() => index <= maxStep && goTo(index)}
                            >
                                {index === 0 && useCartIcon ? (
                                    <Icon {...styles.cartIcon} title={item} />
                                ) : (
                                    <Typography as="div">{translate(item)}</Typography>
                                )}
                            </Typography>
                        );
                    })}
            </Typography>
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Checkout - Shipping",
    allowedContexts: [CheckoutShippingPageContext, CheckoutReviewAndSubmitPageContext],
    displayName: "Checkout Stepper",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutStepper)),
    definition,
};

export default widgetModule;
